import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../../api/client';

export default function AdminNotifications() {
  // 발송 폼
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetMode, setTargetMode] = useState('all'); // 'all' | 'selected'
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sendResult, setSendResult] = useState(null);

  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-push-tokens'],
    queryFn: async () => {
      const result = await adminAPI.getPushTokens();
      if (!result.success) throw new Error(result.error || '토큰 목록 조회 실패');
      return result;
    },
  });

  const users = data?.users || [];
  const totalTokens = data?.totalTokens || 0;

  const sendMutation = useMutation({
    mutationFn: (payload) => adminAPI.sendNotification(payload),
    onSuccess: (result) => {
      setSendResult(result);
      if (result.success && result.invalidRemoved > 0) {
        queryClient.invalidateQueries({ queryKey: ['admin-push-tokens'] });
      }
    },
    onError: (err) => {
      setSendResult({ success: false, error: err.response?.data?.error || err.message });
    },
  });

  const toggleUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const selectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((u) => u.user.id));
    }
  };

  const handleSend = () => {
    if (!title.trim() || !message.trim()) return;

    if (targetMode === 'selected' && selectedUsers.length === 0) {
      setSendResult({ success: false, error: '발송 대상을 선택해주세요' });
      return;
    }

    setSendResult(null);
    sendMutation.mutate({
      title: title.trim(),
      message: message.trim(),
      targetUserIds: targetMode === 'selected' ? selectedUsers : null,
    });
  };

  if (isLoading) {
    return (
      <div className="text-center py-20">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-tayo" />
        <p className="mt-2 text-sub-text">로딩 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-coral/10 border border-coral/20 rounded-xl p-4">
        <p className="text-sm text-coral">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-charcoal">알림 발송</h2>
        <p className="text-sm text-sub-text mt-1">
          등록된 디바이스 {totalTokens}개 · 사용자 {users.length}명
        </p>
      </div>

      {/* 발송 폼 */}
      <div className="bg-white rounded-2xl shadow-card p-5 space-y-4">
        <h3 className="text-base font-bold text-charcoal">알림 작성</h3>

        <div>
          <label className="block text-sm font-medium text-charcoal mb-1">제목</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="알림 제목을 입력하세요"
            className="w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-tayo/30 focus:border-tayo"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-charcoal mb-1">내용</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="알림 내용을 입력하세요"
            rows={3}
            className="w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-tayo/30 focus:border-tayo resize-none"
          />
        </div>

        {/* 대상 선택 */}
        <div>
          <label className="block text-sm font-medium text-charcoal mb-2">발송 대상</label>
          <div className="flex gap-2">
            <button
              onClick={() => setTargetMode('all')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                targetMode === 'all'
                  ? 'bg-tayo text-white'
                  : 'bg-snow text-sub-text hover:bg-border'
              }`}
            >
              전체 ({totalTokens}개 디바이스)
            </button>
            <button
              onClick={() => setTargetMode('selected')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                targetMode === 'selected'
                  ? 'bg-tayo text-white'
                  : 'bg-snow text-sub-text hover:bg-border'
              }`}
            >
              선택 발송
            </button>
          </div>
        </div>

        {/* 발송 버튼 */}
        <button
          onClick={handleSend}
          disabled={sendMutation.isPending || !title.trim() || !message.trim()}
          className="w-full py-3 bg-tayo text-white rounded-xl font-bold text-sm hover:bg-tayo-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {sendMutation.isPending ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              발송 중...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
              </svg>
              알림 발송
            </>
          )}
        </button>

        {/* 발송 결과 */}
        {sendResult && (
          <div
            className={`rounded-xl p-4 ${
              sendResult.success
                ? 'bg-emerald-50 border border-emerald-200'
                : 'bg-coral/10 border border-coral/20'
            }`}
          >
            {sendResult.success ? (
              <div className="text-sm text-emerald-700">
                <p className="font-bold">발송 완료</p>
                <p className="mt-1">
                  {sendResult.targetUsers}명 · {sendResult.sent}/{sendResult.totalTokens}개 디바이스 성공
                  {sendResult.invalidRemoved > 0 && (
                    <span className="text-amber-600"> · 만료 토큰 {sendResult.invalidRemoved}개 정리됨</span>
                  )}
                </p>
                {sendResult.details && (
                  <div className="mt-2 space-y-1">
                    {sendResult.details.map((d, i) => (
                      <p key={i} className={`text-xs font-mono ${d.status === 'sent' ? 'text-emerald-600' : 'text-coral'}`}>
                        {d.token} → {d.status}{d.error ? ` (${d.error})` : ''}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-coral">{sendResult.error}</p>
            )}
          </div>
        )}
      </div>

      {/* 사용자 목록 (선택 발송 모드) */}
      {targetMode === 'selected' && (
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h3 className="text-base font-bold text-charcoal">발송 대상 선택</h3>
            <button
              onClick={selectAll}
              className="text-xs text-tayo hover:text-tayo-dark font-medium"
            >
              {selectedUsers.length === users.length ? '전체 해제' : '전체 선택'}
            </button>
          </div>
          <div className="divide-y divide-border">
            {users.map((item) => (
              <label
                key={item.user.id}
                className="flex items-center gap-4 px-5 py-3 hover:bg-snow transition-colors cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(item.user.id)}
                  onChange={() => toggleUser(item.user.id)}
                  className="w-4 h-4 rounded border-border text-tayo focus:ring-tayo/30"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-charcoal">
                    {item.user.student_id}
                    {item.user.name && (
                      <span className="text-sub-text font-normal ml-2">{item.user.name}</span>
                    )}
                  </p>
                  <p className="text-xs text-sub-text">
                    디바이스 {item.tokens.length}개 · 최근{' '}
                    {new Date(item.tokens[0]?.updated_at).toLocaleDateString('ko-KR')}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* 등록 현황 (전체 모드) */}
      {targetMode === 'all' && (
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="text-base font-bold text-charcoal">알림 등록 현황</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-snow">
                <tr>
                  {['학번', '이름', '디바이스', '최근 등록'].map((h) => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-medium text-charcoal uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-border">
                {users.map((item) => (
                  <tr key={item.user.id} className="hover:bg-snow transition-colors">
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal font-medium">
                      {item.user.student_id}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal">
                      {item.user.name || '-'}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal">
                      {item.tokens.length}개
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-sub-text">
                      {new Date(item.tokens[0]?.updated_at).toLocaleString('ko-KR', {
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
